import { formatDate } from '@angular/common';
import { toLocalDate } from '@quipex/core/primitives';
import {
    DocumentDateConfidence,
    type DocumentDateConfidence as DocumentDateConfidenceValue,
} from '@quipex/shared/contracts';
import type { QpxMetadataItem } from '../metadata';

export interface DocumentDatePresentationSource {
    readonly createdAt: Date | string;
    readonly issueDate?: Date | string | null;
    readonly issueDateConfidence?: DocumentDateConfidenceValue | null;
    readonly expiryDate?: Date | string | null;
    readonly expiryDateConfidence?: DocumentDateConfidenceValue | null;
}

export interface DocumentDateConfidenceInfo {
    readonly label: string;
    readonly icon: string;
    readonly ariaLabel: string;
    readonly context: 'destructive' | 'caution' | 'success';
}

export interface DocumentDateInfo {
    readonly id: 'issue-date' | 'expiry-date' | 'uploaded-date';
    readonly kind: string;
    readonly value: string;
    readonly icon: string;
    readonly ariaLabel: string;
    readonly confidence: DocumentDateConfidenceInfo | null;
}

export function toDocumentDateInfos(
    source: DocumentDatePresentationSource,
    locale: string
): readonly DocumentDateInfo[] {
    const dateInfos: DocumentDateInfo[] = [];

    if (source.issueDate) {
        dateInfos.push(
            toDateInfo({
                id: 'issue-date',
                kind: 'Issued',
                date: source.issueDate,
                icon: 'lucideCalendarCheck2',
                confidence: source.issueDateConfidence,
                locale,
            })
        );
    }

    if (source.expiryDate) {
        dateInfos.push(
            toDateInfo({
                id: 'expiry-date',
                kind: 'Expires',
                date: source.expiryDate,
                icon: 'lucideCalendarX2',
                confidence: source.expiryDateConfidence,
                locale,
            })
        );
    }

    if (!source.issueDate && !source.expiryDate) {
        const value = toDateLabel(source.createdAt, locale);

        dateInfos.push({
            id: 'uploaded-date',
            kind: 'Uploaded',
            value,
            icon: 'lucideCalendarArrowUp',
            confidence: null,
            ariaLabel: `Uploaded ${value}`,
        });
    }

    return dateInfos;
}

export function toDocumentDateMetadataItems(
    source: DocumentDatePresentationSource,
    locale: string
): readonly QpxMetadataItem[] {
    return toDocumentDateInfos(source, locale).map((date) => ({
        id: date.id,
        icon: date.icon,
        label: date.kind,
        value: date.value,
        ariaLabel: date.ariaLabel,
        ...(date.confidence
            ? {
                  trailingIcon: date.confidence.icon,
                  trailingIconAriaLabel: date.confidence.ariaLabel,
                  trailingIconLabel: date.confidence.label,
                  trailingIconContext: date.confidence.context,
              }
            : {}),
    }));
}

function toConfidenceInfo(
    confidence: DocumentDateConfidenceValue | null | undefined
): DocumentDateConfidenceInfo | null {
    switch (confidence) {
        case DocumentDateConfidence.Low:
            return {
                label: 'Low confidence',
                icon: 'lucideSparkles',
                ariaLabel: 'Low confidence',
                context: 'destructive',
            };
        case DocumentDateConfidence.Medium:
            return {
                label: 'Medium confidence',
                icon: 'lucideSparkles',
                ariaLabel: 'Medium confidence',
                context: 'caution',
            };
        case DocumentDateConfidence.High:
            return {
                label: 'High confidence',
                icon: 'lucideSparkles',
                ariaLabel: 'High confidence',
                context: 'success',
            };
        default:
            return null;
    }
}

function toDateInfo(options: {
    id: DocumentDateInfo['id'];
    kind: string;
    date: Date | string;
    icon: string;
    confidence: DocumentDateConfidenceValue | null | undefined;
    locale: string;
}): DocumentDateInfo {
    const value = toDateLabel(options.date, options.locale);
    const confidence = toConfidenceInfo(options.confidence);

    return {
        id: options.id,
        kind: options.kind,
        value,
        icon: options.icon,
        confidence,
        ariaLabel: confidence
            ? `${options.kind} ${value}. ${confidence.ariaLabel}`
            : `${options.kind} ${value}`,
    };
}

function toDateLabel(value: Date | string, locale: string): string {
    return formatDate(
        typeof value === 'string' ? toLocalDate(value) : value,
        'd MMM yyyy',
        locale
    );
}
